import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import dayjs from 'dayjs';
import { DAY_WIDTH, isValidBookingResize } from '../constants';
import type { Booking, BookingResizeEdge, Machine } from '../types';

interface BookingResizeDrag {
  bookingId: string;
  edge: BookingResizeEdge;
  deltaDays: number;
}

interface UseBookingResizeParams {
  machine: Machine;
  isReadOnly: boolean;
  onResize: (booking: Booking, edge: BookingResizeEdge, newDate: string) => void;
  onBlocked: () => void;
}

interface UseBookingResizeReturn {
  drag: BookingResizeDrag | null;
  canResize: () => boolean;
  startDrag: (e: PointerEvent, booking: Booking, edge: BookingResizeEdge) => void;
  moveDrag: (e: PointerEvent) => void;
  endDrag: (e: PointerEvent) => void;
  cancelDrag: () => void;
}

// Drives the planning grid's booking-resize gesture: grabbing the start/end edge of a booking
// bar and dragging shifts start_date/end_date by whole days. Nothing is written until pointer
// release — moveDrag only updates the ghost's deltaDays, clamped to the last valid position
// while the current pointer position would produce an invalid date (overlap, machine window,
// inversion). A drag smaller than half a day-column rounds to deltaDays = 0, which reads as
// "no change" at release — this is the interaction's only safety mechanism, no separate mode
// toggle or confirmation step. There's deliberately no gating on a booking's pickup/hand-in
// state — BookingModal already lets any booking's dates be edited regardless of that state,
// so restricting the drag handles the same way would just be a stricter, inconsistent path to
// the same data.
export function useBookingResize({
  machine,
  isReadOnly,
  onResize,
  onBlocked,
}: UseBookingResizeParams): UseBookingResizeReturn {
  const [drag, setDrag] = useState<BookingResizeDrag | null>(null);
  const startXRef = useRef(0);
  const lastValidDeltaRef = useRef(0);
  const blockedRef = useRef(false);

  const canResize = () => !isReadOnly;

  const startDrag = (e: PointerEvent, booking: Booking, edge: BookingResizeEdge) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      (e.target as Element).setPointerCapture(e.pointerId);
    } catch {
      /* not supported in this environment (e.g. jsdom) — dragging still works via bound handlers */
    }
    startXRef.current = e.clientX;
    lastValidDeltaRef.current = 0;
    blockedRef.current = false;
    setDrag({ bookingId: booking.id, edge, deltaDays: 0 });
  };

  const moveDrag = (e: PointerEvent) => {
    if (!drag) return;
    const booking = machine.bookings?.find((b) => b.id === drag.bookingId);
    if (!booking) return;
    const rawDeltaDays = Math.round((e.clientX - startXRef.current) / DAY_WIDTH);
    if (rawDeltaDays === drag.deltaDays) return;
    const baseDate = drag.edge === 'start' ? booking.start_date : booking.end_date;
    const candidateDate = dayjs(baseDate).add(rawDeltaDays, 'day').format('YYYY-MM-DD');
    if (isValidBookingResize(booking, drag.edge, candidateDate, machine)) {
      lastValidDeltaRef.current = rawDeltaDays;
      blockedRef.current = false;
      setDrag((d) => (d ? { ...d, deltaDays: rawDeltaDays } : d));
    } else if (!blockedRef.current) {
      blockedRef.current = true;
      onBlocked();
    }
  };

  const endDrag = (e: PointerEvent) => {
    try {
      (e.target as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* not supported in this environment (e.g. jsdom) */
    }
    if (drag && lastValidDeltaRef.current !== 0) {
      const booking = machine.bookings?.find((b) => b.id === drag.bookingId);
      if (booking) {
        const baseDate = drag.edge === 'start' ? booking.start_date : booking.end_date;
        const newDate = dayjs(baseDate).add(lastValidDeltaRef.current, 'day').format('YYYY-MM-DD');
        onResize(booking, drag.edge, newDate);
      }
    }
    setDrag(null);
  };

  // Aborts the in-progress drag without committing anything, however far it moved —
  // triggered by Escape or a right-click (see the effect below). The eventual native
  // pointerup (left button release) still fires moveDrag/endDrag via pointer capture,
  // but both already no-op once drag is null.
  const cancelDrag = useCallback(() => {
    lastValidDeltaRef.current = 0;
    blockedRef.current = false;
    setDrag(null);
  }, []);

  useEffect(() => {
    if (!drag) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelDrag();
    };
    // Right-click cancels too — also suppress the native context menu, which would
    // otherwise pop up over the grid mid-drag.
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      cancelDrag();
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('contextmenu', handleContextMenu);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [drag, cancelDrag]);

  return { drag, canResize, startDrag, moveDrag, endDrag, cancelDrag };
}
